import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LOGO_URL = "https://static.readdy.ai/image/76abf0d92622af9fc4827e62ca023833/9bd36fccc1442f12a415c76af596da08.jpeg";

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f2027 0%, #1a3a2a 50%, #0f2027 100%)" }}>
      {/* Dot grid overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #6ee7b7 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />
      {/* Top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-24 opacity-20 pointer-events-none" style={{ background: "radial-gradient(ellipse, #34d399 0%, transparent 70%)" }} />

      {/* Top accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-12">
          {/* Brand */}
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl overflow-hidden border border-emerald-500/30">
                <img src={LOGO_URL} alt="Sonic Express Services Logo" className="w-full h-full object-cover" />
              </div>
              <span className="font-bold text-lg text-white">{t("nav_brand")}</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">{t("footer_desc")}</p>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-shield-check-line text-emerald-400 text-xs" />
                </div>
                <span className="text-emerald-400 text-xs font-medium">Secure Tracking</span>
              </div>
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-time-line text-emerald-400 text-xs" />
                </div>
                <span className="text-emerald-400 text-xs font-medium">Real-time Updates</span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
              <a href="#contact">{t("footer_contact")}</a>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                  <i className="ri-mail-line text-emerald-400 text-sm" />
                </div>
                <div>
                  <p className="text-slate-500 text-xs mb-0.5">Email us</p>
                  <a
                    href="mailto:sonicexpresservices@gmail.com"
                    className="text-slate-300 text-sm hover:text-emerald-400 transition-colors cursor-pointer"
                  >
                    {t("footer_email")}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="text-slate-400 text-sm hover:text-emerald-400 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-emerald-500" />
                  </div>
                  Home
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-400 text-sm hover:text-emerald-400 transition-colors cursor-pointer flex items-center gap-2">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-emerald-500" />
                  </div>
                  {t("footer_privacy")}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 text-sm hover:text-emerald-400 transition-colors cursor-pointer flex items-center gap-2">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-emerald-500" />
                  </div>
                  {t("footer_terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-700/60 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-sm">
            &copy; {year} <span className="text-slate-400">{t("nav_brand")}</span>. {t("footer_rights")}
          </p>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-slate-500 text-xs">All systems operational</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
